/**
 * PytestFlakeCard — Command Centre live flake indicator.
 * Jul 2026
 *
 * Sits under the CI Health badge. Answers one question in one glance:
 *   "Which pytest tests are silently burning auto-retry budget this week?"
 *
 * Chip colours:
 *   OK      → 1-2 reruns · absorbed cleanly by pytest-rerunfailures
 *   WATCH   → 3-4 reruns · keep an eye on it before it fails outright
 *   HIGH    → 5+ reruns  · fix it or archive it
 *
 * Auto-refreshes every 60s. Renders `null` on quiet weeks so the section
 * disappears entirely — no clutter when the suite is clean. Same data
 * source as the Monday Systems Status email's Pytest Flake Health section
 * (`GET /api/admin/pytest-flakes/health?days=7`).
 */
import React from 'react';
import { Activity, RefreshCw } from 'lucide-react';
import { useAdminHealthCache } from '../../hooks/useAdminHealthCache';

const REFRESH_MS = 60 * 1000;

const CHIP = {
  HIGH: { bg: '#FEE2E2', fg: '#DC2626', label: 'HIGH' },
  WATCH: { bg: '#FEF3C7', fg: '#B45309', label: 'WATCH' },
  OK: { bg: '#D1FAE5', fg: '#059669', label: 'OK' },
};

function chipFor(count) {
  if (count >= 5) return CHIP.HIGH;
  if (count >= 3) return CHIP.WATCH;
  return CHIP.OK;
}

export const PytestFlakeCard = () => {
  // useAdminHealthCache: shared 30s TTL + optimistic revalidation across
  // every admin tile that consumes the same endpoint. See
  // /app/frontend/src/hooks/useAdminHealthCache.js for the design intent.
  const { data, refetch } = useAdminHealthCache(
    '/api/admin/pytest-flakes/health?days=7',
    { pollMs: REFRESH_MS },
  );
  const [refreshing, setRefreshing] = React.useState(false);

  const load = React.useCallback(async () => {
    setRefreshing(true);
    try { await refetch(); } finally { setRefreshing(false); }
  }, [refetch]);

  // Quiet-week vanish: no data yet OR zero flakes → render nothing.
  const flakes = data?.flakes || [];
  if (!data || flakes.length === 0) return null;

  const maxCount = flakes[0]?.rerun_count || 0;
  const accent = maxCount >= 5 ? '#DC2626' : maxCount >= 3 ? '#F59E0B' : '#10B981';
  const bg = maxCount >= 5 ? '#FEF2F2' : maxCount >= 3 ? '#FFFBEB' : '#F0FAF3';
  const NAVY = '#0F2A55';

  const runsWithReruns = data.runs_with_reruns || 0;
  const totalRuns = data.total_runs || 0;

  return (
    <div
      data-testid="pytest-flake-card"
      className="p-3 rounded-lg"
      style={{ backgroundColor: bg, border: `2px solid ${NAVY}` }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${accent}20` }}
          >
            <Activity size={16} style={{ color: accent }} />
          </div>
          <div>
            <div
              data-testid="pytest-flake-card-headline"
              className="text-sm font-bold"
              style={{ color: '#1A1209' }}
            >
              Pytest flakes · {flakes.length} test{flakes.length !== 1 ? 's' : ''} auto-retried
            </div>
            <div className="text-xs" style={{ color: '#44403A' }}>
              {runsWithReruns} of {totalRuns} run{totalRuns !== 1 ? 's' : ''} used auto-retry · last 7 days
            </div>
          </div>
        </div>
        <button
          data-testid="pytest-flake-card-refresh"
          onClick={load}
          disabled={refreshing}
          className="p-1.5 rounded hover:bg-white transition-colors disabled:opacity-50"
          title="Refresh flake counts"
        >
          <RefreshCw
            size={14}
            className={refreshing ? 'animate-spin' : ''}
            style={{ color: '#44403A' }}
          />
        </button>
      </div>

      <div className="space-y-1.5">
        {flakes.slice(0, 3).map((f) => {
          const chip = chipFor(f.rerun_count);
          const short = f.test_id.includes('::') ? f.test_id.split('::').slice(-1)[0] : f.test_id;
          return (
            <div
              key={f.test_id}
              data-testid={`pytest-flake-row-${f.test_id}`}
              className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-white/70"
            >
              <span
                className="text-xs font-mono truncate"
                style={{ color: '#1A1209' }}
                title={f.test_id}
              >
                {short}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs font-semibold" style={{ color: '#1A1209' }}>
                  {f.rerun_count}×
                </span>
                <span
                  className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider"
                  style={{ backgroundColor: chip.bg, color: chip.fg }}
                >
                  {chip.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {flakes.length > 3 && (
        <div className="text-xs mt-2" style={{ color: '#44403A' }}>
          + {flakes.length - 3} more · full list arrives in Monday's Systems Status email
        </div>
      )}
    </div>
  );
};

export default PytestFlakeCard;
